<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

defineOptions({
  name: 'AppModal',
})

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title: string
    subtitle?: string
    closeOnBackdrop?: boolean
    variant?: 'default' | 'image-preview'
  }>(),
  {
    subtitle: '',
    closeOnBackdrop: true,
    variant: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const modalRef = ref<HTMLElement | null>(null)
const titleId = useId()

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const variantClass = computed(() =>
  props.variant === 'image-preview' ? 'app-modal--image-preview' : '',
)

function close() {
  open.value = false
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector =
    'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'

  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.getClientRects().length > 0,
  )
}

function onKeydown(event: KeyboardEvent) {
  if (!open.value) return

  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }

  if (event.key !== 'Tab') return

  const container = modalRef.value
  if (!container) return

  const focusables = getFocusableElements(container)
  if (!focusables.length) {
    event.preventDefault()
    container.focus()
    return
  }

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement

  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
    return
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

const lastActiveElement = ref<HTMLElement | null>(null)
const previousBodyOverflow = ref<string>('')

watch(
  () => props.modelValue,
  async (isOpen) => {
    if (!import.meta.client) return

    if (isOpen) {
      lastActiveElement.value =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      previousBodyOverflow.value = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      await nextTick()
      const container = modalRef.value
      if (!container) return

      const focusables = getFocusableElements(container)
      if (focusables.length) {
        focusables[0].focus()
        return
      }
      container.focus()
      return
    }

    document.body.style.overflow = previousBodyOverflow.value || ''
    if (lastActiveElement.value) lastActiveElement.value.focus()
    lastActiveElement.value = null
  },
)

onBeforeUnmount(() => {
  if (!import.meta.client) return
  document.body.style.overflow = previousBodyOverflow.value || ''
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="app-modal-overlay"
      @click.self="closeOnBackdrop ? close() : undefined"
    >
      <div
        ref="modalRef"
        :class="['app-modal', variantClass]"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        @keydown="onKeydown"
      >
        <header class="app-modal__header">
          <div>
            <h2 :id="titleId" class="app-modal__title">
              {{ title }}
            </h2>
            <p v-if="subtitle" class="app-modal__subtitle lp-body">
              {{ subtitle }}
            </p>
          </div>

          <button
            type="button"
            class="app-icon-btn app-modal__close"
            aria-label="Stäng"
            @click="close"
          >
            <AppIcon name="hero-x-mark" size="md" />
          </button>
        </header>

        <div class="app-modal__body">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="app-modal__footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>
